import * as Sentry from '@sentry/react';

/**
 * Reports failed write requests to Sentry.
 *
 * Every dashboard mutation goes through a hook that turns a rejected request
 * into a toast. That is fine for the user and invisible to us: the rejection is
 * handled, so nothing ever reaches Sentry, and a broken endpoint looks exactly
 * like a healthy one from the error stream. This interceptor is the missing
 * half — it observes the failure and re-rejects, so the toast path is unchanged.
 *
 * Reads are deliberately excluded. A failed GET usually re-renders as an empty
 * or loading state and retries on its own; a failed write is a user action that
 * did not happen.
 */

const MUTATION_METHODS = ['post', 'put', 'patch', 'delete'];

/**
 * Statuses we see constantly by design, and which say nothing about a defect.
 *
 * 401 is a lapsed Clerk session — routine, and it self-heals on refresh.
 * 403 is very often deliberate: several revenue-critical endpoints return it on
 * purpose when a staff member is impersonating a retailer (see the subscribe /
 * cancel / card-checkout handlers), so staff doing normal "view as" support
 * work would otherwise generate a steady drip of non-issues.
 *
 * Everything else is reported, including 4xx — a 400 from our own dashboard
 * usually means we sent a payload the API rejects, which is a real bug.
 */
const UNREPORTED_STATUSES = [401, 403];

/**
 * Read-shaped POSTs: reads whose query travels as a POST body. They sit behind
 * SWR and retry on failure, so reporting them turns one upstream incident into
 * a sustained event stream per open tab — the read exclusion above, applied to
 * the routes the verb filter cannot see. Matched against the normalised route.
 */
const UNREPORTED_ROUTES = ['/api/insights', '/api/inventory/:id/batch'];

/**
 * Collapses per-resource ids out of a path so one broken endpoint is one Sentry
 * issue rather than one per shop or per product.
 *
 * Without this, `/api/billing/status/{uuid}` fingerprints as a distinct issue
 * for every retailer who hits it, which buries the fact that it is one outage.
 */
export function normalisePath(path = '') {
	return path
		.replace(/\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g, '/:id')
		.replace(/\/\d+/g, '/:id')
		.replace(/\/(?=[^/]*\d)[A-Za-z0-9._-]{6,}/g, '/:id');
}

function pathOf(url = '') {
	try {
		// Request URLs are usually relative ('/api/…'), which the URL constructor
		// rejects without a base — the base here is only ever used to parse.
		return new URL(url, 'https://dashboard.invalid').pathname;
	} catch {
		return url;
	}
}

/** API Gateway echoes its request id under one of these, depending on payload version. */
function awsRequestIdOf(headers) {
	if (!headers) return undefined;
	const get = typeof headers.get === 'function' ? (k) => headers.get(k) : (k) => headers[k];
	return get('x-amzn-requestid') || get('apigw-requestid') || get('x-amz-apigw-id') || undefined;
}

export function shouldReport({ method, status, route }) {
	if (!method || !MUTATION_METHODS.includes(method.toLowerCase())) return false;
	if (typeof status !== 'number') return false;
	if (status < 400) return false;
	if (UNREPORTED_ROUTES.includes(route)) return false;
	return !UNREPORTED_STATUSES.includes(status);
}

/**
 * Builds the Sentry event for a failed mutation without sending it, so the
 * shape can be asserted in a test.
 */
export function describeFailedMutation(error) {
	const method = (error?.config?.method || '').toLowerCase();
	const status = error?.response?.status;
	const path = pathOf(error?.config?.url);
	const route = normalisePath(path);

	return {
		method,
		status,
		path,
		route,
		awsRequestId: awsRequestIdOf(error?.response?.headers),
		// Group by what broke, not by who hit it.
		fingerprint: ['api-mutation-failed', method.toUpperCase(), route, String(status)],
		// A 5xx is ours; a 4xx is usually a bad payload and less urgent.
		level: status >= 500 ? 'error' : 'warning'
	};
}

export function reportFailedMutation(error, { sentry = Sentry } = {}) {
	const details = describeFailedMutation(error);

	if (!shouldReport(details)) return false;

	sentry.withScope((scope) => {
		scope.setLevel(details.level);
		scope.setFingerprint(details.fingerprint);
		scope.setTag('http.method', details.method.toUpperCase());
		scope.setTag('http.status', String(details.status));
		scope.setTag('api.route', details.route);
		if (details.awsRequestId) {
			// Lets a frontend event be joined to its backend counterpart once the
			// backend Sentry pilot (NEA-6262) lands.
			scope.setTag('aws.requestId', details.awsRequestId);
		}
		scope.setContext('request', { method: details.method.toUpperCase(), path: details.path, status: details.status });
		sentry.captureException(error);
	});

	error.mutationReported = true;

	return true;
}

/**
 * True when this reporter already captured the error, so call sites that also
 * capture in their own catch can skip the duplicate — the two events carry
 * different fingerprints, so Sentry's dedupe cannot collapse them. 401/403 and
 * no-response failures are never marked: for those the call site's capture is
 * the only event.
 */
export function alreadyReported(error) {
	return Boolean(error?.mutationReported);
}

/**
 * `beforeErrorSampling` gate for the dashboards' buffer-mode replay
 * integration. Without it every reporter capture flushes a Session Replay —
 * the priciest Sentry unit — making routine 4xx write bounces the dominant
 * replay source. 5xx reporter events keep their replay: those are our outages.
 */
export function shouldSampleReplay(event) {
	if (event?.fingerprint?.[0] !== 'api-mutation-failed') return true;
	return event.level === 'error';
}

/**
 * Attaches the reporter to an axios instance.
 *
 * Observe-only: the rejection is always passed through unchanged, so every
 * existing catch/toast path behaves exactly as before.
 */
export function attachMutationReporter(client, { sentry = Sentry } = {}) {
	client.interceptors.response.use(
		(response) => response,
		(error) => {
			try {
				reportFailedMutation(error, { sentry });
			} catch {
				// Reporting must never be able to turn a handled API error into an
				// unhandled one in the dashboard.
			}
			return Promise.reject(error);
		}
	);

	return client;
}
